import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  EnrollSourceType,
  ExperienceAnchorType,
  IExperience,
  IExperienceTemplate,
  PARTICIPANT_ACTOR_KEY
} from '@dadohr/shared/domainTypes/experience';
import {
  DiffSet,
  IUser,
  IUserImportEffects,
  IUserImportMeta,
  UserImportEffect,
  UserImportEffectExpAnchorChange,
  UserImportEffectExpEnrollment,
  UserImportEffectExpSupportingActorChange,
  UserImportEffectStatus,
  UserImportEffectType,
  UserImportStatus
} from '@dadohr/shared/domainTypes/user';
import { css } from '@emotion/css';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  Tooltip,
  Typography
} from '@material-ui/core';
import { assertNever } from 'assert-never';
import { capitalize, compact, get } from 'lodash';
import {
  AlertCircle,
  Check,
  Eye,
  LucideIcon as Icon,
  Info,
  Rocket,
  User,
  X
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Color, ColoredChipLight } from '../../../../../components/ColoredChip';
import {
  ID_TO_ROW_KEY,
  ItemSorters,
  ROW_HEIGHTS,
  RowsRenderer
} from '../../../../../components/GroupableList';
import { InfoBox } from '../../../../../components/InfoBox';
import { Loader } from '../../../../../components/Loader';
import { SomethingWentWrong } from '../../../../../components/SomethingWentWrong';
import { IColumn } from '../../../../../components/Table/VirtualizedSortable';
import {
  TFunction,
  useTranslation,
  useUnsafeTranslation
} from '../../../../../i18next';
import { ReactComponent as CalendarBackward } from '../../../../../icons/calendar-backward.svg';
import { ReactComponent as CalendarForward } from '../../../../../icons/calendar-forward.svg';
import { Section, SectionTitle } from '../../../../../layout/Section';
import { captureException } from '../../../../../sentry';
import {
  repositionAnchorForInstance,
  updateActorForInstance,
  useExperienceInstanceMapByIds
} from '../../../../../services/experiences/instance';
import { InstanceRecalculateReason } from '../../../../../services/experiences/recalcualateInstance';
import {
  createExperienceInstance,
  useExperienceTemplateMapByIds
} from '../../../../../services/experiences/template';
import { getAnchorOffsetForExperience } from '../../../../../services/experiences/timing';
import {
  combineLoadingValues3,
  updateDoc,
  useMappedLoadingValue
} from '../../../../../services/firebase';
import {
  addToTs,
  now,
  Timestamp,
  toMoment
} from '../../../../../services/time';
import {
  matchUser,
  toFullName,
  UNKNOWN_USER,
  useUserMapById
} from '../../../../../services/user';
import { useImportEffects } from '../../../../../services/user/import';
import { AnchorField } from '../../../../Experiences/components/Anchors';
import { ParticipantDialog } from '../../../../Experiences/pages/Details/ParticipantDialog';
import {
  UserTaskTimeline,
  UserTaskTimelineHeading
} from '../../../components/UserTaskTimeline';

type D = UserImportEffect;

type O = {
  instancesById: { [key: string]: Doc<IExperience> | null };
  tplsById: { [key: string]: Doc<IExperienceTemplate> | null };
  usersById: { [key: string]: Doc<IUser> | null };
  onChange: (nextEffect: UserImportEffect) => void;
  anchorFields: AnchorField[];
  importDate: Timestamp;
  importStatus: UserImportStatus;
  orgDateFormat: string;
};

const EffectsWrapper = styled('div')((p) => ({
  display: 'flex',
  flexDirection: 'column'
}));

const SubEffectsWrapper = styled('div')((p) => ({
  display: 'flex'
}));

const IconWrapper = styled('span')((p) => ({
  display: 'flex',
  alignItems: 'center',
  marginRight: p.theme.spacing(0.5),
  padding: `${p.theme.spacing(0.4)}px ${p.theme.spacing(0.5)}px`,
  backgroundColor: p.theme.custom.colors.gray.w200,
  borderRadius: p.theme.custom.border.radius(0.7)
}));

const InfoWrapper = styled('div')((p) => ({
  width: 'fit-content',
  marginLeft: p.theme.spacing(3.5),
  marginTop: p.theme.spacing(0.7)
}));

const InnerInfoWrapper = styled('div')((p) => ({
  display: 'flex',
  alignItems: 'center'
}));

const InfoText = styled(Typography)<{
  textColor: string;
}>`
  font-size: ${(p) => p.theme.custom.fontSize.s}px;
  color: ${(p) => p.textColor} !important;
  padding-left: ${(p) => p.theme.spacing(0.5)}px;
`;

const ActionsButtonWrapper = styled('div')((p) => ({
  display: 'flex'
}));

const IconComponent = ({
  tooltipText,
  icon
}: {
  tooltipText: string;
  icon: React.FunctionComponent<{ size: any; color: any }>;
}) => {
  const theme = useTheme();
  return (
    <Tooltip title={tooltipText}>
      <IconWrapper>
        {React.createElement(icon, {
          size: 13,
          color: theme.custom.colors.gray.w500
        })}
      </IconWrapper>
    </Tooltip>
  );
};

const InfoComponent = ({
  type,
  color,
  icon,
  infoText,
  chipColor
}: {
  type?: string;
  color: string;
  icon: Icon;
  infoText?: string;
  chipColor: Color;
}) => {
  return (
    <InfoWrapper>
      <ColoredChipLight color={chipColor} variant="squared">
        <InnerInfoWrapper>
          {React.createElement(icon, {
            size: 13,
            color: color
          })}
          <InfoText textColor={color} variant="caption">
            {infoText}
          </InfoText>
        </InnerInfoWrapper>
      </ColoredChipLight>
    </InfoWrapper>
  );
};

const ReEnrollmentChip = ({ active }: { active?: boolean }) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  if (!active) {
    return null;
  }
  return (
    <InfoComponent
      infoText={t('Re-enrollment')}
      color={theme.custom.colors.blue.w700}
      icon={Info}
      chipColor="blue"
    />
  );
};

const EffectDescription = ({
  d,
  o,
  verbose
}: {
  d: D;
  o: O;
  verbose?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const { importStatus, importDate } = o;
  const referenceMoment = useMemo(
    () => toMoment(importStatus === UserImportStatus.DONE ? importDate : now()),
    [importStatus, importDate]
  );
  const theme = useTheme();
  const tpl = o.tplsById[d.tplId];
  if (d.type === UserImportEffectType.EXP_ENROLLMENT) {
    const startMoment = toMoment(d.startDate);

    if (startMoment.isBefore(referenceMoment, 'day')) {
      const diff = referenceMoment.diff(startMoment, 'days');

      return (
        <EffectsWrapper>
          <SubEffectsWrapper>
            {verbose && (
              <IconComponent
                icon={Rocket}
                tooltipText={t('Enroll to experience')}
              />
            )}
            <Typography variant="body1">
              {t('import.effects.startHint', {
                count: diff
              })}
            </Typography>
          </SubEffectsWrapper>
          {verbose && (
            <InfoComponent
              infoText={t('import.effects.catchupHint', {
                count: diff
              })}
              color={
                d.status === UserImportEffectStatus.DECLINED
                  ? theme.custom.colors.red.w700
                  : theme.custom.colors.blue.w700
              }
              icon={AlertCircle}
              chipColor={
                d.status === UserImportEffectStatus.DECLINED ? 'red' : 'blue'
              }
            />
          )}
          {verbose && <ReEnrollmentChip active={!!d.isReEnrollment} />}
        </EffectsWrapper>
      );
    }

    return (
      <EffectsWrapper>
        <SubEffectsWrapper>
          {verbose && (
            <IconComponent
              icon={Rocket}
              tooltipText={t('Enroll to experience')}
            />
          )}
          <Typography variant="body1">
            {t(`Start experience on {{ startDate }}`, {
              startDate: toMoment(d.startDate).format('YYYY/MM/DD HH:mm')
            })}
          </Typography>
        </SubEffectsWrapper>

        {verbose && <ReEnrollmentChip active={!!d.isReEnrollment} />}
      </EffectsWrapper>
    );
  }
  if (d.type === UserImportEffectType.EXP_SUPPORTING_ACTOR_CHANGE) {
    if (!tpl) {
      console.warn('TEMPLATE_NOT_FOUND', { d, o });
      return null;
    }
    const actor = tpl.data.supportingActors[d.affectedActor.key];
    if (!actor) {
      console.warn('ACTOR_NOT_FOUND', { d, o, tpl });
      return null;
    }
    const prev = d.affectedActor.prevUserId
      ? o.usersById[d.affectedActor.prevUserId] || UNKNOWN_USER()
      : null;
    const next = o.usersById[d.affectedActor.nextUserId] || UNKNOWN_USER();
    return (
      <EffectsWrapper>
        <SubEffectsWrapper>
          {verbose && (
            <IconComponent
              icon={User}
              tooltipText={t('Change supporting actor')}
            />
          )}
          <Typography variant="body1">
            {t('Change {{ role }} from {{ prev }} to {{ next }}', {
              role: actor.name,
              prev: prev ? toFullName(prev.data) : t('no one'),
              next: toFullName(next.data)
            })}
          </Typography>
        </SubEffectsWrapper>
        {verbose && d.rejectionReason && (
          <InfoComponent
            infoText={d.rejectionReason}
            color={theme.custom.colors.red.w700}
            icon={Info}
            chipColor={'red'}
          />
        )}
      </EffectsWrapper>
    );
  }

  if (d.type === UserImportEffectType.EXP_ANCHOR_CHANGE) {
    const n = now();
    const diffInDays = toMoment(d.anchor.next).diff(toMoment(n), 'day');
    //TODO: Cross check this and perhaps add effect status to be accurate
    const checkPastDays = diffInDays < -14;
    const checkFutureDays = diffInDays > 31;

    if (!tpl) {
      console.warn('TEMPLATE_NOT_FOUND', { d, o });
      return null;
    }
    const { anchor } = tpl.data;
    if (!anchor) {
      console.warn('ANCHOR_NOT_FOUND', { d, o, tpl });
      return null;
    }
    if (anchor.type !== ExperienceAnchorType.EMPLOYEE_DATE) {
      console.warn('ANCHOR_TYPE_NOT_IMPLEMENTED', { d, o, tpl, anchor });
      return null;
    }
    const anchorField = o.anchorFields.find((a) => a.field === anchor.field);
    if (!anchorField) {
      console.warn('ANCHOR_FIELD_NOT_FOUND', { d, o, tpl, anchor });
      return null;
    }

    const isManualOverride =
      d.rejectionReason ===
      'Manually overridden dates are not updated by imports';

    return (
      <EffectsWrapper>
        <SubEffectsWrapper>
          {verbose && (
            <IconComponent
              icon={checkPastDays ? CalendarBackward : CalendarForward}
              tooltipText={t('Change anchor date')}
            />
          )}
          <Typography variant="body1">
            {t('Change {{ anchorName }} to {{ nextDate }}', {
              anchorName: `${capitalize(anchorField.shortLabel(t))}`,
              nextDate: toMoment(d.anchor.next).format('YYYY/MM/DD')
            })}
          </Typography>
        </SubEffectsWrapper>
        {verbose &&
          (isManualOverride ? (
            <InfoComponent
              infoText={d.rejectionReason}
              color={theme.custom.colors.red.w700}
              icon={Info}
              chipColor={'red'}
            />
          ) : checkPastDays ? (
            <InfoComponent
              infoText={t('import.effects.dateCountPast', {
                count: Math.abs(diffInDays)
              })}
              color={theme.custom.colors.red.w700}
              icon={Info}
              chipColor={'red'}
            />
          ) : checkFutureDays ? (
            <InfoComponent
              infoText={t('import.effects.dateCount', {
                count: Math.abs(diffInDays)
              })}
              color={theme.custom.colors.yellow.w900}
              icon={Info}
              chipColor={'yellow'}
            />
          ) : null)}
      </EffectsWrapper>
    );
  }

  console.warn('UNKNOWN_EFFECT', { d, o });
  return null;
};

const getConfig = (
  status: UserImportEffectStatus,
  t: TFunction,
  importStatus?: UserImportStatus
): [string, Color] => {
  switch (status) {
    case UserImportEffectStatus.AWAITING_APPROVAL:
      if (importStatus === UserImportStatus.DONE) {
        return [t('import.effects.status.declined'), 'red'];
      }
      return [t('import.effects.status.approval'), 'yellow'];
    case UserImportEffectStatus.DECLINED:
      return [t('import.effects.status.declined'), 'red'];
    case UserImportEffectStatus.ACCEPTED:
      return [t('import.effects.status.approved'), 'blue'];
    case UserImportEffectStatus.COMMITTED:
      return [t('import.effects.status.commited'), 'green'];
    default:
      return assertNever(status);
  }
};

const Status = ({
  status,
  width = 'auto',
  importStatus
}: {
  status: UserImportEffectStatus;
  width?: number | string;
  importStatus?: UserImportStatus;
}) => {
  const { t } = useTranslation();
  const [label, color] = useMemo(() => getConfig(status, t, importStatus), [
    status,
    t,
    importStatus
  ]);
  return (
    <ColoredChipLight size="small" color={color} width={width}>
      {label}
    </ColoredChipLight>
  );
};

const TabContainer = styled('div')((p) => ({
  marginTop: p.theme.spacing(4),
  marginBottom: p.theme.spacing(4),
  background: p.theme.palette.background.paper
}));

const TabPanel: React.FC<{ active: boolean }> = ({ active, children }) => {
  return (
    <div role="tabpanel" hidden={!active}>
      {active && <TabContainer>{children}</TabContainer>}
    </div>
  );
};

const STATUS_WIDTH = 120;

const PreviewDialogTitle = styled('div')((p) => ({
  display: 'flex',
  alignItems: 'center',
  gap: p.theme.spacing()
}));

const TimelineHeadingContainer = styled('div')((p) => ({
  marginBottom: p.theme.spacing(2)
}));

const TimelinePreview = ({
  d,
  tpl,
  user,
  anchorFields
}: {
  d: Doc<IExperience>;
  tpl: Doc<IExperienceTemplate>;
  user: Doc<IUser> | null;
  anchorFields: AnchorField[];
}) => {
  return (
    <>
      <TimelineHeadingContainer>
        <UserTaskTimelineHeading d={d} tpl={tpl} user={user} />
      </TimelineHeadingContainer>
      <UserTaskTimeline
        d={d}
        tpl={tpl}
        variant="normal"
        anchorFields={anchorFields}
      />
    </>
  );
};

const EnrollmentPreview = ({
  d,
  o
}: {
  d: UserImportEffectExpEnrollment;
  o: O;
}) => {
  const tpl = o.tplsById[d.tplId];
  const user = o.usersById[d.participantId];

  const { startDate } = d;
  const instance = useMemo(() => {
    if (!tpl || !user) {
      return null;
    }

    const anchorDate = addToTs(
      startDate,
      getAnchorOffsetForExperience(tpl.data.anchor)
    );
    const actors: { key: string; user: Doc<IUser> | null }[] = [
      {
        key: PARTICIPANT_ACTOR_KEY,
        user: user
      },
      ...compact(
        Object.values(tpl.data.supportingActors).map((a) => {
          if (a.type === 'staticUser') {
            const userId =
              a.ruleBasedUserIds.find((r) =>
                matchUser(user, r.rule, anchorDate)
              )?.userId || a.defaultUserId;
            return {
              key: a.key,
              user: o.usersById[userId] || null
            };
          }
          if (a.type === 'user' && a.source === 'PROFILE') {
            const otherUserId = get(user.data as any, a.field);
            if (otherUserId && typeof otherUserId === 'string') {
              return {
                key: a.key,
                user: o.usersById[otherUserId] || null
              };
            }
          }
          console.warn('UNHANDLED_ACTOR', a);
          return null;
        })
      )
    ];
    return createExperienceInstance(tpl, actors, startDate, now(), {
      type: EnrollSourceType.UNKNOWN
    });
  }, [tpl, startDate, user, o.usersById]);

  if (!user) {
    console.warn('USER_NOT_FOUND', d);
  }

  if (!tpl) {
    // instance is always defined when there's a tpl
    console.warn('TPL_NOT_FOUND', d);
    return null;
  }
  if (!instance) {
    // instance is always defined when there's a tpl and a user
    return null;
  }

  return (
    <TimelinePreview
      d={instance}
      tpl={tpl}
      user={user}
      anchorFields={o.anchorFields}
    />
  );
};

const AnchorChangePreview = ({
  d,
  o
}: {
  d: UserImportEffectExpAnchorChange;
  o: O;
}) => {
  const instance = o.instancesById[d.expId];
  const tpl = o.tplsById[d.tplId];
  const user = o.usersById[d.participantId];

  const { anchor } = d;
  const { importDate } = o;
  const updatedInstance = useMemo(() => {
    if (!instance || !tpl || !user) {
      return null;
    }
    // TODO make sure this user is present
    return repositionAnchorForInstance(
      instance,
      tpl,
      user,
      anchor.next,
      importDate,
      InstanceRecalculateReason.INSTANCE_CHANGE
    );
  }, [instance, tpl, anchor, importDate, user]);

  if (!instance) {
    console.warn('INSTANCE_NOT_FOUND', d);
    return null;
  }

  if (!updatedInstance) {
    console.warn('FAILED_TO_UPDATE_ANCHOR', { d, instance });
    return null;
  }

  if (!tpl) {
    console.warn('TPL_NOT_FOUND', d);
    return null;
  }

  return (
    <TimelinePreview
      d={updatedInstance}
      tpl={tpl}
      user={user}
      anchorFields={o.anchorFields}
    />
  );
};

const SupportingActorChangePreview = ({
  d,
  o
}: {
  d: UserImportEffectExpSupportingActorChange;
  o: O;
}) => {
  const instance = o.instancesById[d.expId];
  const tpl = o.tplsById[d.tplId];
  const user = o.usersById[d.participantId];

  const { affectedActor } = d;
  const updatedInstance = useMemo(() => {
    if (!instance || !tpl || !user) {
      return null;
    }
    return updateActorForInstance(
      tpl,
      instance,
      user,
      affectedActor.key,
      affectedActor.nextUserId
    );
  }, [instance, tpl, user, affectedActor]);

  if (!instance) {
    console.warn('INSTANCE_NOT_FOUND', d);
    return null;
  }

  if (!updatedInstance) {
    console.warn('FAILED_TO_UPDATE_ANCHOR', { d, instance });
    return null;
  }

  if (!tpl) {
    console.warn('TPL_NOT_FOUND', d);
    return null;
  }

  return (
    <TimelinePreview
      d={updatedInstance}
      tpl={tpl}
      user={user}
      anchorFields={o.anchorFields}
    />
  );
};

const BeforePreview = ({ d, o }: { d: D; o: O }) => {
  if (!d.expId) {
    // nothing to preview here!
    return null;
  }
  const instance = o.instancesById[d.expId];
  const tpl = o.tplsById[d.tplId];
  const user = o.usersById[d.participantId];

  if (!instance) {
    console.warn('INSTANCE_NOT_FOUND', d);
    return null;
  }

  if (!tpl) {
    console.warn('TPL_NOT_FOUND', d);
    return null;
  }

  return (
    <TimelinePreview
      d={instance}
      tpl={tpl}
      user={user}
      anchorFields={o.anchorFields}
    />
  );
};

const PreviewDialog = ({
  d,
  o,
  open,
  onClose
}: {
  d: D;
  o: O;
  open: boolean;
  onClose: () => void;
  // goToNext? goToPrev? - allow to cycle through them
}) => {
  const { t } = useUnsafeTranslation();
  const [tab, setTab] = useState<'BEFORE' | 'AFTER'>('AFTER');
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle disableTypography>
        <PreviewDialogTitle>
          <Typography component="h3" variant="h3">
            {t('Effect Preview')}
          </Typography>
          <Status status={d.status} />
        </PreviewDialogTitle>
      </DialogTitle>
      <DialogContent>
        <InfoBox>
          <EffectDescription d={d} o={o} />
        </InfoBox>
        <Tabs
          value={tab}
          onChange={(ev, v) => setTab(v)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab
            value={'BEFORE'}
            label={`${t('Before')}`}
            wrapped
            disabled={d.type === UserImportEffectType.EXP_ENROLLMENT}
          />
          <Tab value={'AFTER'} label={`${t('After')}`} wrapped />
        </Tabs>
        <TabPanel active={tab === 'BEFORE'}>
          <BeforePreview d={d} o={o} />
        </TabPanel>
        <TabPanel active={tab === 'AFTER'}>
          {d.type === UserImportEffectType.EXP_ENROLLMENT && (
            <EnrollmentPreview d={d} o={o} />
          )}
          {d.type === UserImportEffectType.EXP_ANCHOR_CHANGE && (
            <AnchorChangePreview d={d} o={o} />
          )}
          {d.type === UserImportEffectType.EXP_SUPPORTING_ACTOR_CHANGE && (
            <SupportingActorChangePreview d={d} o={o} />
          )}
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('Close')}</Button>
        {o.importStatus !== UserImportStatus.DONE &&
          d.status !== UserImportEffectStatus.DECLINED && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                o.onChange({ ...d, status: UserImportEffectStatus.DECLINED });
                onClose();
                // maybe open next?
              }}
            >
              {t('Reject effect')}
            </Button>
          )}
        {o.importStatus !== UserImportStatus.DONE &&
          d.status !== UserImportEffectStatus.ACCEPTED && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                o.onChange({ ...d, status: UserImportEffectStatus.ACCEPTED });
                onClose();
                // maybe open next?
              }}
            >
              {t('Approve effect')}
            </Button>
          )}
      </DialogActions>
    </Dialog>
  );
};

const WrappedParticipantDialog = ({
  d,
  o,
  open,
  onClose
}: {
  d: D;
  o: O;
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const instance = o.instancesById[d.expId];
  const tpl = o.tplsById[d.tplId];
  const hasMissingData = !instance || !tpl;

  // Report to Sentry when effect shows as committed but instance/template is missing
  useEffect(() => {
    if (hasMissingData) {
      captureException(
        new Error(
          'Import effect preview failed: committed effect missing instance or template'
        ),
        {
          tags: {
            feature: 'import_effects',
            action: 'preview_committed_effect'
          },
          extra: {
            effectId: d.id,
            effectType: d.type,
            effectStatus: d.status,
            expId: d.expId,
            tplId: d.tplId,
            participantId: d.participantId,
            hasInstance: !!instance,
            hasTemplate: !!tpl,
            instancesLoadedCount: Object.keys(o.instancesById).length,
            templatesLoadedCount: Object.keys(o.tplsById).length
          },
          level: 'error'
        }
      );
    }
  }, [hasMissingData, d, instance, tpl, o.instancesById, o.tplsById]);

  if (hasMissingData) {
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogContent>
          <SomethingWentWrong height={300} />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('Close')}</Button>
        </DialogActions>
      </Dialog>
    );
  }
  return (
    <ParticipantDialog d={instance} tpl={tpl} open={open} onClose={onClose} />
  );
};

const PreviewButton = () => {
  const { t } = useUnsafeTranslation();
  return (
    <Button size="small" variant="outlined" startIcon={<Eye size={16} />}>
      {t('Preview')}
    </Button>
  );
};

const Actions = ({ d, o }: { d: D; o: O }) => {
  const { t } = useUnsafeTranslation();
  return (
    <ActionsButtonWrapper>
      {d.status !== UserImportEffectStatus.ACCEPTED && (
        <Button
          fullWidth
          size="small"
          variant="outlined"
          onClick={(ev) => {
            ev.stopPropagation();
            o.onChange({ ...d, status: UserImportEffectStatus.ACCEPTED });
          }}
          startIcon={<Check size={16} />}
          className={css({
            marginRight: '3px !important'
          })}
        >
          {t('Approve')}
        </Button>
      )}
      {d.status !== UserImportEffectStatus.DECLINED && (
        <Button
          fullWidth
          size="small"
          variant="outlined"
          onClick={(ev) => {
            ev.stopPropagation();
            o.onChange({ ...d, status: UserImportEffectStatus.DECLINED });
          }}
          startIcon={<X size={16} />}
        >
          {t('Reject')}
        </Button>
      )}
    </ActionsButtonWrapper>
  );
};

type Column = IColumn<D, ColumnName, O>;
type ColumnName =
  | 'participant'
  | 'experience'
  | 'effect'
  | 'status'
  | 'actions'
  | 'preview';
const COLUMNS: Column[] = [
  {
    key: 'participant',
    head: () => 'Participant',
    cell: (d, o) => {
      const p = o.usersById[d.participantId];
      return (
        p && (
          <div>
            {toFullName(p.data) || p.data.email || p.data.personalEmail}
          </div>
        )
      );
    },
    sortable: true,
    align: 'left',
    width: 120,
    flexGrow: 1
  },
  {
    key: 'experience',
    head: () => 'Experience',
    cell: (d, o) => {
      const tpl = o.tplsById[d.tplId];
      return tpl && <strong>{tpl.data.adminTitle || tpl.data.title}</strong>;
    },
    sortable: true,
    align: 'left',
    width: 120,
    flexGrow: 1
  },

  {
    key: 'effect',
    head: () => 'Effect',
    cell: (d, o) => <EffectDescription d={d} o={o} verbose />,
    align: 'left',
    sortable: false,
    defaultDirection: 'asc',
    width: 100,
    flexGrow: 1.5,
    showOverflowOnHover: false
  },
  {
    key: 'status',
    head: () => 'Status',
    cell: (d, o) => {
      const statusChip = (
        <Status
          width={STATUS_WIDTH}
          status={d.status}
          importStatus={o.importStatus}
        />
      );
      if (o.importStatus === UserImportStatus.DONE) {
        return (
          <Tooltip title="Effects cannot be approved or rejected after an import is applied. Please manually enroll users or change/delete experiences as needed from the Experience page.">
            <span>{statusChip}</span>
          </Tooltip>
        );
      }
      if (d.rejectionReason) {
        return (
          <Tooltip title={d.rejectionReason}>
            <span>{statusChip}</span>
          </Tooltip>
        );
      }
      return statusChip;
    },
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: STATUS_WIDTH + 30,
    flexGrow: 0
  },
  {
    key: 'actions',
    head: () => 'Actions',
    cell: (d, o) => {
      return <Actions d={d} o={o} />;
    },
    align: 'left',
    sortable: false,
    defaultDirection: 'asc',
    width: 190,
    flexGrow: 0,
    showOverflowOnHover: false
  },
  {
    key: 'preview',
    head: () => ' ',
    cell: (d, o) => {
      return <PreviewButton />;
    },
    align: 'left',
    sortable: false,
    defaultDirection: 'asc',
    width: 110,
    flexGrow: 0,
    showOverflowOnHover: false
  }
];

const SORTERS: ItemSorters<D, O> = {
  participant: {
    key: 'participant',
    items: {
      sort: (d, o) => {
        const p = o.usersById[d.participantId];
        return p ? toFullName(p.data) : '';
      },
      dir: 'asc'
    }
  },
  experience: {
    key: 'experience',
    items: {
      sort: (d, o) => {
        const tpl = o.tplsById[d.tplId];
        return tpl ? tpl.data.adminTitle || tpl.data.title : '';
      },
      dir: 'asc'
    }
  },
  status: {
    key: 'stats',
    items: {
      sort: (d) => {
        switch (d.status) {
          case UserImportEffectStatus.AWAITING_APPROVAL:
            return 0;
          case UserImportEffectStatus.DECLINED:
            return 1;
          case UserImportEffectStatus.ACCEPTED:
            return 2;
          case UserImportEffectStatus.COMMITTED:
            return 3;
          default:
            return assertNever(d.status);
        }
      },
      dir: 'asc'
    }
  }
};
const DEFAULT_SORTER = SORTERS.participant;

const COLUMNS_WITHOUT_ACTIONS = COLUMNS.filter((c) => c.key !== 'actions');
const COLUMNS_WITHOUT_PREVIEW = COLUMNS.filter(
  (c) => c.key !== 'actions' && c.key !== 'preview'
);

const EffectsInner = ({
  d,
  fx,
  diffSet,
  anchorFields,
  orgDateFormat
}: {
  d: Doc<IUserImportMeta>;
  fx: Doc<IUserImportEffects>;
  diffSet: DiffSet;
  anchorFields: AnchorField[];
  orgDateFormat: string;
}) => {
  const [previewDialogArgs, setPreviewDialogArgs] = useState<{
    d: D;
    o: O;
  } | null>(null);
  const [participantDialogArgs, setParticipantDialogArgs] = useState<{
    d: D;
    o: O;
  } | null>(null);
  const { tplIds, expIds, userIds } = useMemo(() => {
    const tplIds: Set<string> = new Set();
    const expIds: Set<string> = new Set();
    const userIds: Set<string> = new Set();

    fx.data.effects.forEach((f) => {
      tplIds.add(f.tplId);
      f.expId && expIds.add(f.expId); // can be empty for pending enrollments
      userIds.add(f.participantId);
      if (f.type === UserImportEffectType.EXP_SUPPORTING_ACTOR_CHANGE) {
        if (f.affectedActor.prevUserId) {
          userIds.add(f.affectedActor.prevUserId);
        }
        userIds.add(f.affectedActor.nextUserId);
      }
    });
    return {
      tplIds: [...tplIds],
      expIds: [...expIds],
      userIds: [...userIds]
    };
  }, [fx]);

  const collector = useCallback(
    ([instancesById, tplsById, usersById]: [
      { [key: string]: Doc<IExperience> | null },
      { [key: string]: Doc<IExperienceTemplate> | null },
      { [key: string]: Doc<IUser> }
    ]) => {
      const updatedUsers: { [key: string]: Doc<IUser> | null } = {};
      diffSet.added.forEach((u) => (updatedUsers[u.id] = u));
      diffSet.changed.forEach(({ aft }) => (updatedUsers[aft.id] = aft));
      diffSet.removed.forEach((u) => (updatedUsers[u.id] = null)); // better replace with a removed user standin
      return {
        instancesById,
        tplsById,
        usersById: {
          ...usersById,
          ...updatedUsers
        }
      };
    },
    [diffSet]
  );

  const [data, loading] = useMappedLoadingValue(
    combineLoadingValues3(
      useExperienceInstanceMapByIds(expIds),
      useExperienceTemplateMapByIds(tplIds),
      useUserMapById(d.data.orgId, userIds)
    ),
    collector,
    true
  );

  const importDate =
    d.data.committedAt || d.data.previewableAt || d.data.createdAt;
  const importStatus = d.data.status;

  const otherProps = useMemo(() => {
    if (!data) {
      return null;
    }
    return {
      ...data,
      onChange: async (nextFx: UserImportEffect) => {
        const i = fx.data.effects.findIndex((f) => f.id === nextFx.id);
        if (i !== -1) {
          const nextEffects = fx.data.effects.slice();
          nextEffects[i] = nextFx;
          await updateDoc(fx, () => ({ effects: nextEffects }));
        }
      },
      importDate,
      importStatus,
      anchorFields,
      orgDateFormat
    };
  }, [data, fx, anchorFields, importDate, importStatus, orgDateFormat]);

  // Prevents effect rows from being clickable once the import is applied (no preview/participant modal)
  const handleRowClick = useCallback(
    (rowD: D, o: O) => {
      if (d.data.status === UserImportStatus.DONE) return;
      if (rowD.status === UserImportEffectStatus.COMMITTED) {
        setParticipantDialogArgs({ d: rowD, o });
      } else {
        setPreviewDialogArgs({ d: rowD, o });
      }
    },
    [d.data.status]
  );

  if (loading) {
    return <Loader />;
  }

  if (!data || !otherProps) {
    return null;
  }
  return (
    <>
      <RowsRenderer
        variant="contained"
        columns={
          d.data.status === UserImportStatus.PREVIEW
            ? COLUMNS
            : d.data.status === UserImportStatus.DONE
            ? COLUMNS_WITHOUT_PREVIEW
            : COLUMNS_WITHOUT_ACTIONS
        }
        rows={fx.data.effects}
        rowToKey={ID_TO_ROW_KEY}
        sorter={DEFAULT_SORTER}
        sortDirection={DEFAULT_SORTER.items.dir}
        onRowClick={
          d.data.status === UserImportStatus.DONE ? undefined : handleRowClick
        }
        renderHead={true}
        chunkSize={30}
        rootMargin="400px"
        otherProps={otherProps}
        rowHeight={ROW_HEIGHTS.dense}
        headProps={{ sticky: true }}
        cellProps={{ adjustHeight: true }}
      />
      {previewDialogArgs && (
        <PreviewDialog
          d={previewDialogArgs.d}
          o={previewDialogArgs.o}
          open={true}
          onClose={() => setPreviewDialogArgs(null)}
        />
      )}
      {participantDialogArgs && (
        <WrappedParticipantDialog
          d={participantDialogArgs.d}
          o={participantDialogArgs.o}
          open={true}
          onClose={() => setParticipantDialogArgs(null)}
        />
      )}
    </>
  );
};

const TitleContainer = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: p.theme.spacing()
}));

const ButtonContainer = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: p.theme.spacing()
}));

const FxActions = ({ fx }: { fx: Doc<IUserImportEffects> }) => {
  const { t } = useUnsafeTranslation();
  const { canApprove, canDecline } = useMemo(() => {
    return {
      canApprove: !!fx.data.effects.find(
        (f) => f.status !== UserImportEffectStatus.ACCEPTED
      ),
      canDecline: !!fx.data.effects.find(
        (f) => f.status === UserImportEffectStatus.ACCEPTED
      )
    };
  }, [fx]);

  const updateAllStatuses = (nextStatus: UserImportEffectStatus) =>
    updateDoc(fx, (d) => ({
      effects: d.effects.map((e) => ({ ...e, status: nextStatus }))
    }));

  return (
    <ButtonContainer>
      <Button
        variant="outlined"
        onClick={() => updateAllStatuses(UserImportEffectStatus.ACCEPTED)}
        size="small"
        disabled={!canApprove}
        startIcon={<Check size={16} />}
      >
        {t('Approve all')}
      </Button>

      <Button
        variant="outlined"
        onClick={() => updateAllStatuses(UserImportEffectStatus.DECLINED)}
        size="small"
        disabled={!canDecline}
        startIcon={<X size={16} />}
      >
        {t('Reject all')}
      </Button>
    </ButtonContainer>
  );
};

export const Effects = ({
  d,
  diffSet,
  anchorFields,
  orgDateFormat
}: {
  d: Doc<IUserImportMeta>;
  diffSet: DiffSet;
  anchorFields: AnchorField[];
  orgDateFormat: string;
}) => {
  const { t } = useTranslation();
  const [fx, loading] = useImportEffects(d.id);

  if (loading) {
    return (
      <Section>
        <SectionTitle>
          <Typography variant="h4">{t('import.effects.title')}</Typography>
          <Typography variant="body1" color="textSecondary">
            {t('import.effects.subtitle')}
          </Typography>
        </SectionTitle>
        <Loader height={200} />
      </Section>
    );
  }

  if (!fx) {
    return null;
  }

  return (
    <Section>
      <SectionTitle>
        <TitleContainer>
          <div>
            <Typography variant="h4">{t('import.effects.title')}</Typography>
            <Typography variant="body1" color="textSecondary">
              {t('import.effects.subtitle')}
            </Typography>
          </div>
          {d.data.status === UserImportStatus.PREVIEW && <FxActions fx={fx} />}
        </TitleContainer>
      </SectionTitle>
      <EffectsInner
        d={d}
        fx={fx}
        diffSet={diffSet}
        anchorFields={anchorFields}
        orgDateFormat={orgDateFormat}
      />
    </Section>
  );
};
